import { useQuery } from '@tanstack/react-query';
import { Suspense, useMemo, useState } from 'react';

import { BarCompare, LineTrend, ParetoChart } from '../../components/charts';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { ExportMenu } from '../../components/ExportMenu';
import { PageHeader } from '../../components/PageHeader';
import {
  getProductDetail,
  getProductSales,
  listCategories,
  type ProductSalesRow,
} from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtCompact,
  fmtInt,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { useDateRange } from '../../lib/useDateRange';

type Metric = 'amount' | 'profit' | 'qty';
type Mover = 'all' | 'up' | 'down';

export const deltaFrac = (cur: number, prev: number): number => {
  if (prev !== 0) return (cur - prev) / Math.abs(prev);
  return cur === 0 ? 0 : cur > 0 ? 1 : -1;
};
export const shareValue = (metric: Metric, value: number): number =>
  metric === 'profit' ? Math.max(0, value) : value;
const round1 = (n: number): number => Math.round(n * 10) / 10;
const chartSkeleton = (
  <div
    className="animate-pulse bg-bg-warm rounded-card"
    style={{ height: CHART_H.md }}
  />
);

interface ExportRow {
  rank: number;
  name: string;
  category: string;
  baseUnit: string;
  qty: number;
  netAmountBaht: string;
  netAmountSatang: number;
  cogsBaht: string;
  profitBaht: string;
  marginPct: number | '';
  missingCostLines: number;
  sharePct: number;
  deltaPct: number;
}
const exportCols: ExportCol<ExportRow>[] = [
  { key: 'rank', header: 'อันดับ' },
  { key: 'name', header: 'สินค้า' },
  { key: 'category', header: 'หมวด' },
  { key: 'baseUnit', header: 'หน่วยฐาน' },
  { key: 'qty', header: 'จำนวนหน่วยฐาน' },
  { key: 'netAmountBaht', header: 'ยอดขายสุทธิ' },
  { key: 'netAmountSatang', header: 'ยอดขายสุทธิ (satang)' },
  { key: 'cogsBaht', header: 'ต้นทุนขาย' },
  { key: 'profitBaht', header: 'กำไรขั้นต้น' },
  { key: 'marginPct', header: 'Margin %' },
  { key: 'missingCostLines', header: 'รายการที่ต้นทุนไม่ครบ' },
  { key: 'sharePct', header: 'สัดส่วน %' },
  { key: 'deltaPct', header: 'Δ %' },
];

export function ProductSalesView() {
  const { scope, ready } = useBranchScope();
  const { range, prev } = useDateRange();
  const [metric, setMetric] = useState<Metric>('amount');
  const [cat, setCat] = useState('all');
  const [mover, setMover] = useState<Mover>('all');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: cats = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      'report',
      'product-sales',
      scope.branchId,
      scope.allowedBranchIds,
      range.fromDay,
      range.toDay,
    ],
    queryFn: () => getProductSales(scope, range, prev),
    enabled: ready,
  });

  const catName = useMemo(
    () => Object.fromEntries(cats.map((c) => [c.id, c.name])),
    [cats],
  );
  const valOf = (r: ProductSalesRow) =>
    metric === 'amount' ? r.amount : metric === 'profit' ? r.profit : r.qty;
  const prevOf = (r: ProductSalesRow) =>
    metric === 'amount'
      ? r.prevAmount
      : metric === 'profit'
        ? r.prevProfit
        : r.prevQty;
  const metricLabel =
    metric === 'amount'
      ? 'ยอดขายสุทธิ'
      : metric === 'profit'
        ? 'กำไร'
        : 'จำนวน';
  const valueFmt = metric === 'qty' ? fmtInt : fmtBahtShort;
  const tickFmt = metric === 'qty' ? fmtCompact : fmtSatangCompact;

  const visible = useMemo(() => {
    let rows = (data?.rows ?? []).filter((r) => r.qty > 0 || r.prevQty > 0);
    if (cat !== 'all') rows = rows.filter((r) => r.categoryId === cat);
    if (mover !== 'all')
      rows = rows.filter((r) => {
        const d = deltaFrac(valOf(r), prevOf(r));
        return mover === 'up' ? d > 0.001 : d < -0.001;
      });
    return [...rows].sort((a, b) => valOf(b) - valOf(a));
  }, [data, cat, mover, metric]);

  const totalForShare = useMemo(
    () => visible.reduce((sum, row) => sum + shareValue(metric, valOf(row)), 0),
    [visible, metric],
  );

  // KPIs use the full (unfiltered) result so they read as headline figures.
  const amtDelta = deltaFrac(
    data?.totalAmount ?? 0,
    data?.prevTotalAmount ?? 0,
  );
  const marginRate =
    data && data.totalAmount !== 0 ? data.totalProfit / data.totalAmount : null;
  const activeSkuRate =
    data && data.totalSku !== 0 ? data.activeSku / data.totalSku : 0;

  const pareto = useMemo(() => {
    let cum = 0;
    return visible
      .filter((row) => shareValue(metric, valOf(row)) > 0)
      .slice(0, 8)
      .map((r) => {
        const value = shareValue(metric, valOf(r));
        cum += value;
        return {
          label: r.name.split(' ')[0],
          value,
          cum: totalForShare ? round1((cum / totalForShare) * 100) : 0,
        };
      });
  }, [visible, totalForShare, metric]);

  const topBars = useMemo(
    () => visible.slice(0, 8).map((r) => ({ label: r.name, value: valOf(r) })),
    [visible, metric],
  );

  const exportRows: ExportRow[] = visible.map((r, i) => ({
    rank: i + 1,
    name: r.name,
    category: catName[r.categoryId] ?? r.categoryId,
    baseUnit: r.baseUnitName,
    qty: r.qty,
    netAmountBaht: fmtBaht(r.amount),
    netAmountSatang: r.amount,
    cogsBaht: fmtBaht(r.cogs),
    profitBaht: fmtBaht(r.profit),
    marginPct: r.margin == null ? '' : round1(r.margin * 100),
    missingCostLines: r.missingCostLineCount,
    sharePct: totalForShare
      ? round1((shareValue(metric, valOf(r)) / totalForShare) * 100)
      : 0,
    deltaPct: round1(deltaFrac(valOf(r), prevOf(r)) * 100),
  }));

  return (
    <div className="bo-fade mx-auto w-full max-w-[1480px]">
      <PageHeader
        title="ยอดขายและกำไรรายสินค้า"
        subtitle="เห็นว่าสินค้าตัวไหนทำยอด ตัวไหนสร้างกำไร และตัวไหนกำลังเปลี่ยนทิศทาง"
        actions={
          <ExportMenu
            rows={exportRows}
            cols={exportCols}
            filename="ยอดขายและกำไรรายสินค้า"
            title="ยอดขายและกำไรรายสินค้า"
            pdf
          />
        }
      />
      <DateRangeFilter />

      {isError ? (
        <section
          className="mb-6 rounded-[18px] border border-danger/25 bg-danger-light/35 px-5 py-6 sm:px-7"
          role="alert"
        >
          <div className="max-w-2xl">
            <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-danger">
              โหลดรายงานไม่สำเร็จ
            </div>
            <h2 className="mt-2 text-h3 font-bold text-ink">
              ยังดึงข้อมูลยอดขายรายสินค้าไม่ได้
            </h2>
            <p className="mt-1 text-body text-ink-2">
              {error instanceof Error
                ? error.message
                : 'กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง'}
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-4 h-10 rounded-btn bg-ink px-4 text-body font-bold text-white transition duration-300 ease-out hover:bg-ink-2 active:scale-[0.98]"
            >
              ลองโหลดอีกครั้ง
            </button>
          </div>
        </section>
      ) : (
        <>
          <section
            className="mb-6 grid overflow-hidden rounded-[20px] border border-line-2 bg-surface shadow-[0_18px_50px_-36px_rgba(86,16,37,0.45)] lg:grid-cols-[1.2fr_0.8fr]"
            aria-label="สรุปยอดขายรายสินค้า"
          >
            <div className="relative min-h-[230px] overflow-hidden bg-ink px-5 py-6 text-white sm:px-7 sm:py-7 lg:px-8">
              <div
                className="pointer-events-none absolute -bottom-28 -right-16 h-64 w-64 rounded-full border-[42px] border-primary/25"
                aria-hidden="true"
              />
              <div className="relative flex h-full flex-col">
                <div className="text-sm-label font-bold uppercase tracking-[0.14em] text-white/60">
                  ยอดขายสุทธิ
                </div>
                {isLoading ? (
                  <div className="mt-5 h-12 w-56 animate-pulse rounded-card bg-white/10 sm:w-72" />
                ) : (
                  <div className="num mt-4 text-[38px] font-bold leading-none tracking-[-0.05em] sm:text-[48px]">
                    ฿{fmtAmount(data?.totalAmount ?? 0)}
                  </div>
                )}
                <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-8">
                  <div>
                    <div
                      className={`num text-body font-bold ${
                        amtDelta >= 0 ? 'text-[#80D7AB]' : 'text-[#F2A39A]'
                      }`}
                    >
                      {amtDelta >= 0 ? '+' : '−'}
                      {Math.abs(amtDelta * 100).toFixed(1)}%
                    </div>
                    <div className="mt-1 text-sm-label text-white/55">
                      เทียบกับช่วงก่อนหน้า
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="num text-lg font-bold">
                      {fmtInt(data?.totalQty ?? 0)}
                    </div>
                    <div className="text-sm-label text-white/55">
                      หน่วยฐานที่ขาย
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 divide-x divide-y divide-line-2 lg:divide-y-0 lg:grid-cols-1 lg:divide-x-0 lg:grid-rows-3">
              <SummaryMetric
                label="กำไรขั้นต้น"
                value={`฿${fmtAmount(data?.totalProfit ?? 0)}`}
                helper={
                  marginRate == null
                    ? 'ยังคำนวณ Margin ไม่ได้'
                    : `Margin ${(marginRate * 100).toFixed(1)}%`
                }
                loading={isLoading}
              />
              <SummaryMetric
                label="ต้นทุนขาย"
                value={`฿${fmtAmount(data?.totalCogs ?? 0)}`}
                helper="ต้นทุน ณ เวลาที่ขาย"
                loading={isLoading}
              />
              <SummaryMetric
                label="SKU ที่สร้างยอด"
                value={`${fmtInt(data?.activeSku ?? 0)} / ${fmtInt(data?.totalSku ?? 0)}`}
                helper={
                  data?.missingCostLineCount
                    ? `ต้นทุนไม่ครบ ${fmtInt(data.missingCostLineCount)} รายการ`
                    : `${(activeSkuRate * 100).toFixed(0)}% ของสินค้าทั้งหมด`
                }
                warning={Boolean(data?.missingCostLineCount)}
                loading={isLoading}
                className="col-span-2 lg:col-span-1"
              />
            </div>
          </section>

          <section
            className="mb-6 rounded-[18px] border border-line-2 bg-surface px-4 py-4 sm:px-5 sm:py-5"
            aria-labelledby="analysis-menu-title"
          >
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.65fr)] xl:items-end">
              <div>
                <div className="mb-3 flex items-baseline justify-between gap-3">
                  <div>
                    <h2
                      id="analysis-menu-title"
                      className="text-body font-bold text-ink"
                    >
                      เลือกมุมวิเคราะห์
                    </h2>
                    <p className="mt-0.5 text-sm-label text-ink-3">
                      ตารางและกราฟจะเรียงใหม่ตามค่าที่เลือก
                    </p>
                  </div>
                  <span className="hidden text-sm-label font-semibold text-ink-4 sm:inline">
                    จัดอันดับตาม
                  </span>
                </div>
                <div
                  className="grid grid-cols-3 gap-1 rounded-[12px] bg-bg-warm p-1.5"
                  role="group"
                  aria-label="จัดอันดับตาม"
                >
                  {(['amount', 'profit', 'qty'] as Metric[]).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMetric(m)}
                      aria-pressed={metric === m}
                      className={`min-h-12 rounded-[9px] px-2 text-body font-bold transition duration-300 ease-out active:scale-[0.98] sm:px-4 ${
                        metric === m
                          ? 'bg-surface text-primary shadow-[0_5px_16px_-10px_rgba(86,16,37,0.55)] ring-1 ring-line'
                          : 'text-ink-3 hover:bg-surface/60 hover:text-ink'
                      }`}
                    >
                      {m === 'amount'
                        ? 'ยอดขาย'
                        : m === 'profit'
                          ? 'กำไร'
                          : 'จำนวน'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="flex min-w-0 flex-col gap-2">
                  <span className="text-sm-label font-bold text-ink-2">
                    หมวดสินค้า
                  </span>
                  <select
                    value={cat}
                    onChange={(e) => setCat(e.target.value)}
                    aria-label="หมวดสินค้า"
                    className="h-12 min-w-0 rounded-[10px] border border-line bg-surface px-3 text-body font-semibold text-ink outline-none transition duration-300 ease-out hover:border-ink-4 focus:border-primary focus:ring-2 focus:ring-primary/10"
                  >
                    <option value="all">ทุกหมวด</option>
                    {cats.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex min-w-0 flex-col gap-2">
                  <span className="text-sm-label font-bold text-ink-2">
                    การเปลี่ยนแปลง
                  </span>
                  <select
                    value={mover}
                    onChange={(e) => setMover(e.target.value as Mover)}
                    aria-label="มุมมอง"
                    className="h-12 min-w-0 rounded-[10px] border border-line bg-surface px-3 text-body font-semibold text-ink outline-none transition duration-300 ease-out hover:border-ink-4 focus:border-primary focus:ring-2 focus:ring-primary/10"
                  >
                    <option value="all">ทุกทิศทาง</option>
                    <option value="up">ยอดกำลังโต</option>
                    <option value="down">ยอดกำลังลด</option>
                  </select>
                </label>
              </div>
            </div>
          </section>

          <div className="mb-6 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1.3fr)_minmax(340px,0.7fr)]">
            <section className="rounded-[20px] border border-line-2 bg-surface p-4 sm:p-5">
              <div className="mb-2 flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-primary">
                    โครงสร้างยอดขาย
                  </div>
                  <h2 className="mt-1 text-h3 font-bold text-ink">
                    สินค้ากลุ่มไหนทำยอดส่วนใหญ่
                  </h2>
                </div>
                <span className="rounded-chip bg-bg-warm px-3 py-1 text-sm-label font-bold text-ink-3">
                  Pareto 80/20
                </span>
              </div>
              {isLoading ? (
                chartSkeleton
              ) : (
                <Suspense fallback={chartSkeleton}>
                  <ParetoChart
                    data={pareto}
                    valueFormat={valueFmt}
                    tickFormat={tickFmt}
                  />
                </Suspense>
              )}
            </section>

            <section className="rounded-[20px] border border-line-2 bg-surface p-4 sm:p-5">
              <div className="mb-2">
                <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-primary">
                  ผู้นำยอดขาย
                </div>
                <h2 className="mt-1 text-h3 font-bold text-ink">
                  8 อันดับตาม{metricLabel}
                </h2>
              </div>
              {isLoading ? (
                chartSkeleton
              ) : (
                <Suspense fallback={chartSkeleton}>
                  <BarCompare
                    data={topBars}
                    colorByIndex
                    valueFormat={valueFmt}
                    tickFormat={tickFmt}
                  />
                </Suspense>
              )}
            </section>
          </div>

          <section className="overflow-hidden rounded-[20px] border border-line-2 bg-surface">
            <div className="flex flex-col gap-2 border-b border-line-2 px-4 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-5">
              <div>
                <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-primary">
                  อันดับสินค้า
                </div>
                <h2 className="mt-1 text-h3 font-bold text-ink">
                  รายละเอียดรายสินค้า
                </h2>
              </div>
              {!isLoading && (
                <div className="text-sm-label text-ink-3">
                  พบ <b className="num text-ink">{fmtInt(visible.length)}</b>{' '}
                  รายการ · เลือกสินค้าเพื่อดูรายละเอียด
                </div>
              )}
            </div>

            {!ready || isLoading ? (
              <div className="space-y-3 p-5" aria-label="กำลังโหลดรายสินค้า">
                {[0, 1, 2, 3, 4].map((row) => (
                  <div
                    key={row}
                    className="h-12 animate-pulse rounded-[10px] bg-bg-warm"
                    style={{ animationDelay: `${row * 90}ms` }}
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="bo-table min-w-[1120px]">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>สินค้า</th>
                      <th>หมวด</th>
                      <th className="bo-th-num">จำนวนหน่วยฐาน</th>
                      <th className="bo-th-num">ยอดขายสุทธิ</th>
                      <th className="bo-th-num">ต้นทุน</th>
                      <th className="bo-th-num">กำไร</th>
                      <th className="bo-th-num">Margin</th>
                      <th className="bo-th-num">สัดส่วน</th>
                      <th className="bo-th-num">เทียบงวดก่อน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="!px-5 !py-14 text-center">
                          <div className="mx-auto max-w-sm">
                            <div className="text-h3 font-bold text-ink">
                              ไม่พบสินค้าที่ตรงกับตัวกรอง
                            </div>
                            <p className="mt-1 text-body font-normal text-ink-3">
                              ลองเลือกทุกหมวดหรือดูสินค้าทุกทิศทางอีกครั้ง
                            </p>
                            {(cat !== 'all' || mover !== 'all') && (
                              <button
                                type="button"
                                onClick={() => {
                                  setCat('all');
                                  setMover('all');
                                }}
                                className="mt-4 h-10 rounded-btn border border-line bg-surface px-4 text-body font-bold text-primary transition duration-300 ease-out hover:border-primary hover:bg-primary-light active:scale-[0.98]"
                              >
                                ล้างตัวกรอง
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      visible.map((r, i) => {
                        const share = totalForShare
                          ? (shareValue(metric, valOf(r)) / totalForShare) * 100
                          : 0;
                        const d = deltaFrac(valOf(r), prevOf(r));
                        const open = openId === r.productId;
                        return (
                          <Row
                            key={r.productId}
                            r={r}
                            i={i}
                            share={share}
                            delta={d}
                            catName={catName[r.categoryId] ?? r.categoryId}
                            open={open}
                            onToggle={() =>
                              setOpenId(open ? null : r.productId)
                            }
                            colSpan={10}
                            range={range}
                            scope={scope}
                          />
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function SummaryMetric({
  label,
  value,
  helper,
  warning = false,
  loading = false,
  className = '',
}: {
  label: string;
  value: string;
  helper: string;
  warning?: boolean;
  loading?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`flex min-h-[108px] flex-col justify-center px-4 py-4 sm:px-6 ${className}`}
    >
      <div className="text-sm-label font-bold uppercase tracking-[0.1em] text-ink-3">
        {label}
      </div>
      {loading ? (
        <div className="mt-2 h-7 w-32 animate-pulse rounded-card bg-bg-warm" />
      ) : (
        <div className="num mt-1 text-[22px] font-bold leading-tight tracking-[-0.03em] text-ink">
          {value}
        </div>
      )}
      <div
        className={`mt-1 text-sm-label ${
          warning ? 'font-bold text-warn' : 'text-ink-3'
        }`}
      >
        {helper}
      </div>
    </div>
  );
}

function Row({
  r,
  i,
  share,
  delta,
  catName,
  open,
  onToggle,
  colSpan,
  range,
  scope,
}: {
  r: ProductSalesRow;
  i: number;
  share: number;
  delta: number;
  catName: string;
  open: boolean;
  onToggle: () => void;
  colSpan: number;
  range: { fromDay: number; toDay: number };
  scope: { branchId: string | null; allowedBranchIds?: string[] };
}) {
  const dCls =
    delta > 0.001 ? 'text-ok' : delta < -0.001 ? 'text-danger' : 'text-ink-4';
  const dTxt = `${delta > 0 ? '▲ +' : delta < 0 ? '▼ ' : '– '}${Math.abs(delta * 100).toFixed(1)}%`;
  return (
    <>
      <tr
        className={`cursor-pointer ${open ? 'bg-primary-light' : 'hover:bg-bg-warm'}`}
        onClick={onToggle}
        aria-expanded={open}
        title="คลิกเพื่อเจาะลึกรายสินค้า"
      >
        <td>
          <span
            className={`inline-flex w-6 h-6 items-center justify-center rounded-btn text-sm-label font-bold ${i < 3 ? 'bg-primary text-white' : 'bg-bg-warm text-ink-2'}`}
          >
            {i + 1}
          </span>
        </td>
        <td className="font-semibold">{r.name}</td>
        <td>
          <span className="text-sm-label font-semibold px-2 py-0.5 rounded-chip border border-line bg-surface-2 text-ink-2">
            {catName}
          </span>
        </td>
        <td className="bo-td-num num">
          {fmtInt(r.qty)} <span className="text-ink-3">{r.baseUnitName}</span>
        </td>
        <td className="bo-td-num num font-semibold">{fmtAmount(r.amount)}</td>
        <td className="bo-td-num num text-ink-3">{fmtAmount(r.cogs)}</td>
        <td
          className={`bo-td-num num font-semibold ${r.profit < 0 ? 'text-danger' : 'text-ok'}`}
        >
          {fmtAmount(r.profit)}
        </td>
        <td className="bo-td-num num">
          {r.margin == null ? '—' : `${(r.margin * 100).toFixed(1)}%`}
        </td>
        <td className="bo-td-num">
          <span className="inline-flex items-center gap-2 justify-end">
            <span className="relative h-1.5 w-[70px] rounded-full bg-line-2 overflow-hidden hidden sm:inline-block">
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-primary"
                style={{ width: `${Math.min(100, share)}%` }}
              />
            </span>
            <span className="num">{share.toFixed(1)}%</span>
          </span>
        </td>
        <td className={`bo-td-num num font-bold ${dCls}`}>{dTxt}</td>
      </tr>
      {open && (
        <tr>
          <td
            colSpan={colSpan}
            className="!p-0 bg-surface-2 border-b border-line-2"
          >
            <ProductDrilldown
              productId={r.productId}
              name={r.name}
              range={range}
              scope={scope}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ProductDrilldown({
  productId,
  name,
  range,
  scope,
}: {
  productId: string;
  name: string;
  range: { fromDay: number; toDay: number };
  scope: { branchId: string | null; allowedBranchIds?: string[] };
}) {
  const { data, isLoading } = useQuery({
    queryKey: [
      'product-detail',
      productId,
      scope.branchId,
      scope.allowedBranchIds,
      range.fromDay,
      range.toDay,
    ],
    queryFn: () => getProductDetail(productId, scope, range),
  });
  if (isLoading || !data)
    return <div className="h-40 m-4 animate-pulse bg-bg-warm rounded-card" />;
  return (
    <div className="p-4 space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          {
            label: `ขายเทียบ ${data.summary.baseUnitName}`,
            value: fmtInt(data.summary.baseQuantity),
          },
          {
            label: 'ยอดขายสุทธิ',
            value: `฿${fmtAmount(data.summary.netAmount)}`,
          },
          { label: 'ต้นทุนขาย', value: `฿${fmtAmount(data.summary.cogs)}` },
          { label: 'กำไรขั้นต้น', value: `฿${fmtAmount(data.summary.profit)}` },
          {
            label: 'Margin',
            value:
              data.summary.margin == null
                ? '—'
                : `${(data.summary.margin * 100).toFixed(1)}%`,
          },
        ].map((item) => (
          <div
            key={item.label}
            className="rounded-card border border-line bg-surface p-3"
          >
            <div className="text-sm-label text-ink-3">{item.label}</div>
            <div className="num mt-1 text-lg font-bold text-ink">
              {item.value}
            </div>
          </div>
        ))}
      </div>

      {data.summary.missingCostLineCount > 0 && (
        <div className="rounded-card border border-warn bg-warn-light px-3 py-2 text-sm text-warn">
          มี {fmtInt(data.summary.missingCostLineCount)} รายการขายที่ไม่มีต้นทุน
          กำไรที่แสดงอาจสูงกว่าความเป็นจริง
        </div>
      )}

      <div className="overflow-x-auto rounded-card border border-line bg-surface">
        <div className="px-4 py-3 text-sm-label font-bold text-ink-3">
          ขายแยกตามหน่วย — {name}
        </div>
        <table className="bo-table">
          <thead>
            <tr>
              <th>หน่วยขาย</th>
              <th className="bo-th-num">จำนวนที่ขาย</th>
              <th className="bo-th-num">อัตราแปลง</th>
              <th className="bo-th-num">เทียบหน่วยฐาน</th>
              <th className="bo-th-num">ยอดขายสุทธิ</th>
              <th className="bo-th-num">ต้นทุน</th>
              <th className="bo-th-num">กำไร</th>
              <th className="bo-th-num">Margin</th>
            </tr>
          </thead>
          <tbody>
            {data.byUnit.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-ink-3 py-6">
                  ไม่มียอดขายในช่วงเวลานี้
                </td>
              </tr>
            ) : (
              data.byUnit.map((unit) => (
                <tr key={`${unit.unitId}:${unit.unitName}:${unit.ratio}`}>
                  <td className="font-semibold">{unit.unitName}</td>
                  <td className="bo-td-num num">{fmtInt(unit.quantity)}</td>
                  <td className="bo-td-num num">
                    1 {unit.unitName} = {fmtInt(unit.ratio)}{' '}
                    {data.summary.baseUnitName}
                  </td>
                  <td className="bo-td-num num">{fmtInt(unit.baseQuantity)}</td>
                  <td className="bo-td-num num">{fmtAmount(unit.netAmount)}</td>
                  <td className="bo-td-num num">{fmtAmount(unit.cogs)}</td>
                  <td
                    className={`bo-td-num num font-semibold ${unit.profit < 0 ? 'text-danger' : 'text-ok'}`}
                  >
                    {fmtAmount(unit.profit)}
                  </td>
                  <td className="bo-td-num num">
                    {unit.margin == null
                      ? '—'
                      : `${(unit.margin * 100).toFixed(1)}%`}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="text-sm-label font-bold text-ink-3 mb-2">
            แนวโน้มยอดขายสุทธิรายวัน
          </div>
          <Suspense fallback={chartSkeleton}>
            <LineTrend
              data={data.series}
              height={CHART_H.sm}
              valueFormat={fmtBahtShort}
              tickFormat={fmtSatangCompact}
            />
          </Suspense>
        </div>
        <div>
          <div className="text-sm-label font-bold text-ink-3 mb-2">
            แยกตามสาขา
          </div>
          <Suspense fallback={chartSkeleton}>
            <BarCompare
              data={data.byBranch.map((b) => ({
                label: b.branchShort,
                value: b.qty,
              }))}
              height={CHART_H.sm}
              colorByIndex
            />
          </Suspense>
          <div className="mt-3 overflow-x-auto rounded-card border border-line bg-surface">
            <table className="bo-table">
              <thead>
                <tr>
                  <th>สาขา</th>
                  <th className="bo-th-num">หน่วยฐาน</th>
                  <th className="bo-th-num">ยอดขายสุทธิ</th>
                  <th className="bo-th-num">ต้นทุน</th>
                  <th className="bo-th-num">กำไร</th>
                </tr>
              </thead>
              <tbody>
                {data.byBranch.map((branch) => (
                  <tr key={branch.branchId}>
                    <td className="font-semibold">{branch.branchShort}</td>
                    <td className="bo-td-num num">{fmtInt(branch.qty)}</td>
                    <td className="bo-td-num num">
                      {fmtAmount(branch.amount)}
                    </td>
                    <td className="bo-td-num num">{fmtAmount(branch.cogs)}</td>
                    <td
                      className={`bo-td-num num font-semibold ${branch.profit < 0 ? 'text-danger' : 'text-ok'}`}
                    >
                      {fmtAmount(branch.profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
